import React, { useEffect, useMemo, useState } from "react";
import { Modal, Button, Badge } from "@themesberg/react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faMapMarkerAlt,
  faTag,
  faUsers,
  faMobileAlt,
} from "@fortawesome/free-solid-svg-icons";
import { resolveImageUrl, placeholderImageDataUrl } from "../services/api";

const HERO_PLACEHOLDER = placeholderImageDataUrl(800, 480);

function categoryVariant(category) {
  const colors = {
    Food: "danger",
    Events: "primary",
    Outdoors: "success",
    Nightlife: "warning",
    Wellness: "info",
    Art: "secondary",
    "Hidden Gems": "dark",
    Family: "info",
  };
  return colors[category] || "secondary";
}

function formatPrice(price) {
  if (price == null || price === "" || price === "FREE") return "Free";
  return String(price);
}

/**
 * App-style read-only preview from admin edit form values (includes unsaved edits).
 */
export default function ExperienceLivePreviewModal({ show, onHide, experience }) {
  const [imgIdx, setImgIdx] = useState(0);

  const imageUrls = useMemo(() => {
    const imgs = experience?.images;
    if (!Array.isArray(imgs) || imgs.length === 0) return [];
    return imgs.map((u) => resolveImageUrl(u)).filter(Boolean);
  }, [experience?.images]);

  useEffect(() => {
    if (show) setImgIdx(0);
  }, [show, experience?._id, experience?.images]);

  const mainSrc =
    imageUrls.length > 0 ? imageUrls[Math.min(imgIdx, imageUrls.length - 1)] : null;

  if (!experience) return null;

  const title = experience.title?.trim() || "Untitled experience";
  const description =
    experience.description?.trim() || "No description yet.";
  const category = experience.category || "";
  const price = formatPrice(experience.price);
  const address = experience.location?.address?.trim() || "";
  const tags = Array.isArray(experience.tags) ? experience.tags : [];
  const grouptype = Array.isArray(experience.grouptype) ? experience.grouptype : [];
  const state = experience.state || "pending";
  const bookingProvider = experience.bookingProvider || "golocal";

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="lg"
      centered
      scrollable
      enforceFocus={false}
    >
      <Modal.Header closeButton>
        <Modal.Title className="d-flex align-items-center gap-2">
          <FontAwesomeIcon icon={faMobileAlt} className="text-primary" />
          Live preview
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ background: "#e2e8f0", padding: "1.25rem" }}>
        <p className="text-muted small mb-3 mb-md-4">
          Approximate in-app appearance using your <strong>current form values</strong>{" "}
          (including unsaved changes). Save to update what users see.
        </p>

        {state !== "active" && (
          <div
            className="alert alert-warning py-2 px-3 small mb-3"
            role="status"
          >
            State is <strong>{state}</strong> — experiences are only shown in the
            public app when <strong>active</strong>.
          </div>
        )}

        <div
          style={{
            maxWidth: 400,
            margin: "0 auto",
            background: "#fff",
            borderRadius: 24,
            boxShadow: "0 12px 40px rgba(15, 23, 42, 0.18)",
            overflow: "hidden",
            border: "1px solid #cbd5e1",
          }}
        >
          <div style={{ position: "relative", background: "#0f172a" }}>
            <img
              src={mainSrc || HERO_PLACEHOLDER}
              alt=""
              style={{
                width: "100%",
                height: 220,
                objectFit: "cover",
                display: "block",
              }}
              onError={(e) => {
                e.target.src = HERO_PLACEHOLDER;
              }}
            />
            {imageUrls.length > 1 && (
              <div
                style={{
                  display: "flex",
                  gap: 6,
                  padding: "0.65rem 0.75rem",
                  overflowX: "auto",
                  background: "#f8fafc",
                  borderTop: "1px solid #e2e8f0",
                }}
              >
                {imageUrls.map((url, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setImgIdx(i)}
                    style={{
                      padding: 0,
                      border:
                        i === imgIdx
                          ? "2px solid #4f46e5"
                          : "2px solid transparent",
                      borderRadius: 8,
                      flexShrink: 0,
                      cursor: "pointer",
                      background: "none",
                    }}
                  >
                    <img
                      src={url}
                      alt=""
                      style={{
                        width: 56,
                        height: 56,
                        objectFit: "cover",
                        borderRadius: 6,
                        display: "block",
                      }}
                      onError={(e) => {
                        e.target.src = HERO_PLACEHOLDER;
                      }}
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          <div style={{ padding: "1.25rem 1.25rem 1.5rem" }}>
            <div className="d-flex flex-wrap gap-2 align-items-center mb-2">
              {category ? (
                <Badge bg={categoryVariant(category)} style={{ fontWeight: 600 }}>
                  {category}
                </Badge>
              ) : (
                <Badge bg="secondary">Category</Badge>
              )}
              <span
                style={{
                  fontWeight: 700,
                  color: "#4f46e5",
                  fontSize: "0.95rem",
                }}
              >
                {price}
              </span>
            </div>

            <h4
              style={{
                fontSize: "1.35rem",
                fontWeight: 800,
                color: "#0f172a",
                lineHeight: 1.25,
                marginBottom: "0.75rem",
              }}
            >
              {title}
            </h4>

            <p
              style={{
                color: "#475569",
                fontSize: "0.95rem",
                lineHeight: 1.55,
                marginBottom: "1rem",
                whiteSpace: "pre-wrap",
              }}
            >
              {description}
            </p>

            {address ? (
              <div
                className="d-flex gap-2 align-items-start mb-3"
                style={{ fontSize: "0.9rem", color: "#334155" }}
              >
                <FontAwesomeIcon
                  icon={faMapMarkerAlt}
                  className="mt-1"
                  style={{ color: "#94a3b8" }}
                />
                <span>{address}</span>
              </div>
            ) : null}

            {grouptype.length > 0 && (
              <div className="mb-3">
                <div
                  className="d-flex align-items-center gap-2 mb-2"
                  style={{ fontSize: "0.8rem", fontWeight: 700, color: "#64748b" }}
                >
                  <FontAwesomeIcon icon={faUsers} />
                  Good for
                </div>
                <div className="d-flex flex-wrap gap-1">
                  {grouptype.map((g) => (
                    <Badge key={g} bg="secondary" style={{ textTransform: "capitalize" }}>
                      {g}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {tags.length > 0 && (
              <div>
                <div
                  className="d-flex align-items-center gap-2 mb-2"
                  style={{ fontSize: "0.8rem", fontWeight: 700, color: "#64748b" }}
                >
                  <FontAwesomeIcon icon={faTag} />
                  Tags
                </div>
                <div className="d-flex flex-wrap gap-1">
                  {tags.map((t) => (
                    <Badge key={t} pill bg="light" text="dark" className="border">
                      {t}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            <div
              style={{
                marginTop: "1.25rem",
                paddingTop: "1rem",
                borderTop: "1px solid #e2e8f0",
                fontSize: "0.8rem",
                color: "#64748b",
              }}
            >
              Booking:{" "}
              <strong style={{ color: "#334155" }}>
                {bookingProvider === "viator" ? "Viator" : "GoLocal"}
              </strong>
              {bookingProvider === "viator" && experience.viatorProductCode ? (
                <span className="ms-1">· Code {experience.viatorProductCode}</span>
              ) : null}
            </div>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
